import React, { useState, useMemo, useCallback } from 'react';
import { styles } from '../../config/styles';
import { formOptions } from '../../constants/formOptions';
import {
  formatCurrency,
  formatTasaForDisplay,
  formatNumberForInput,
  parseTasaForState,
  formatComitenteForDisplay,
} from '../../utils/formatters';
import { compareValues } from '../../utils/sortHelpers';
import { formatHoraForDisplay } from '../../utils/dateHelpers';
import { computeKpiTotales } from '../../utils/planillaKpis';
import { filterMisClientes } from '../../utils/saldosScope';
import { MONEDA_BUCKETS, MONEDA_MOVIMIENTO, MONEDA_TITULO } from '../../constants/monedas';
import { useHorizontalScrollSync } from '../../hooks/useHorizontalScrollSync';
import { useStackedTable } from '../../hooks/useStackedTable';
import { useMobileCollapse } from '../../hooks/useMobileCollapse';
import TopScrollbar from '../TopScrollbar';
import CollapsibleHeader from '../common/CollapsibleHeader';
import MobileCollapsibleSection from '../common/MobileCollapsibleSection';
import { SortIcon, PencilIcon, TrashIcon } from '../Icons';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import OperacionBadge from '../cauciones/OperacionBadge';
import PaseFormModal from './PaseFormModal';
import PaseOperarModal from './PaseOperarModal';
import {
  softDeletePase,
  setPaseEstado,
  rechazarPase,
  updatePase,
} from '../../services/pasesService';

const IndicatorCard = ({ title, value, currency }) => (
  <div className="axon-kpi-card" style={styles.kpiCard}>
    <h3 style={styles.kpiTitle}>{title}</h3>
    <p className="axon-kpi-value" style={styles.kpiValue}>{formatCurrency(value, currency)}</p>
  </div>
);

// Estilos de la sección de filtros (equivalentes a la planilla de Cauciones).
const filterContainerStyle = {
  marginBottom: '1.5rem', backgroundColor: '#FFFFFF', borderRadius: '0.5rem',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)', borderLeft: `3px solid ${styles.colors.primary}`,
};
const filterHeaderStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '0.75rem 1.25rem', borderBottom: '1px solid #E5E7EB',
};
const filterHeaderLeftStyle = { display: 'flex', alignItems: 'center', gap: '0.5rem' };
const filterTitleStyle = {
  fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem', fontWeight: '600',
  color: styles.colors.primary, margin: 0, textTransform: 'uppercase', letterSpacing: '0.04em',
};
const filterBadgeStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: styles.colors.primary, color: '#FFFFFF', fontSize: '0.65rem',
  fontWeight: '700', borderRadius: '10px', minWidth: '18px', height: '18px', padding: '0 5px',
};
const filterGridStyle = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: '0.75rem', padding: '1rem 1.25rem',
};
const filterGroupStyle = { display: 'flex', flexDirection: 'column', gap: '0.3rem' };
const filterLabelStyle = {
  fontSize: '0.68rem', fontWeight: '600', textTransform: 'uppercase',
  color: styles.colors.mediumGray, letterSpacing: '0.05em',
};
const filterInputStyle = {
  padding: '0.5rem 0.65rem', border: '1px solid #E5E7EB', borderRadius: '0.5rem',
  fontSize: '0.8rem', fontFamily: "'Roboto', sans-serif", boxSizing: 'border-box',
  width: '100%', outline: 'none', color: styles.colors.secondary,
};
const filterSelectFieldStyle = { ...filterInputStyle, backgroundColor: '#FFFFFF', cursor: 'pointer' };
const clearFiltersButtonStyle = {
  padding: '0.4rem 0.85rem', border: 'none', borderRadius: '0.5rem', fontSize: '0.75rem',
  fontFamily: "'Roboto', sans-serif", backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#DC2626',
  cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '0.3rem',
};

const activosBadgeStyle = {
  display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '12px',
  backgroundColor: '#EEF2FF', color: '#3730A3', fontSize: '0.75rem', fontWeight: 600,
  cursor: 'pointer', border: '1px solid #C7D2FE',
};

// La fecha de vencimiento es un string 'YYYY-MM-DD'; se muestra como DD/MM/AAAA.
const formatVencimiento = (isoDate) => {
  if (!isoDate || typeof isoDate !== 'string') return '-';
  const [y, m, d] = isoDate.split('-');
  return y && m && d ? `${d}/${m}/${y}` : isoDate;
};

const tableHeaders = [
  { key: 'persistentId', label: 'ID' },
  { key: 'actions', label: 'Acciones', sortable: false },
  { key: 'createdAt', label: 'Fecha' },
  { key: 'horaCarga', label: 'Hora', sortable: false, narrow: true },
  { key: 'operacion', label: 'Operación' },
  { key: 'nroComitente', label: 'Nro Comitente' }, { key: 'cliente', label: 'Cliente' },
  { key: 'monto', label: 'Monto' }, { key: 'montoOperado', label: 'Monto Operado' },
  { key: 'fechaVencimiento', label: 'Vencimiento' },
  { key: 'tasa', label: 'Tasa' }, { key: 'tasaOperada', label: 'Tasa Operada' },
  { key: 'moneda', label: 'Moneda' }, { key: 'mercado', label: 'Mercado' },
  { key: 'comercial', label: 'Comercial' }, { key: 'operador', label: 'Operador' }, { key: 'estado', label: 'Estado' },
  { key: 'activos', label: 'Activos', sortable: false },
  { key: 'comentarioComercial', label: 'Com. Comercial' },
  { key: 'comentarioTrader', label: 'Com. Trader' },
];

// filtroMisClientes (rol Comercial): filtro de vista "Mis clientes", indicadores incluidos.
const PasesPlanilla = ({ pases, isLoading, userRole, userName, userId, clientesDb, especies, onViewComment, filtroMisClientes = null }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPase, setEditingPase] = useState(null);
  const [operarModal, setOperarModal] = useState({ isOpen: false, pase: null, mode: 'operar', readOnly: false });
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [paseToReject, setPaseToReject] = useState(null);
  const [rejectionComment, setRejectionComment] = useState('');
  const [editingTasaId, setEditingTasaId] = useState(null);
  const [editingTasaValue, setEditingTasaValue] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'descending' });
  const [filters, setFilters] = useState({});
  const filtersPanel = useMobileCollapse();
  const stackedTable = useStackedTable({
    title: 'Cliente',
    primary: ['ID', 'Operación', 'Monto', 'Tasa', 'Vencimiento', 'Activos', 'Estado'],
    actions: 'Acciones',
  });

  const uniqueComerciales = useMemo(() => {
    const values = new Set(pases.map(p => p.comercial).filter(Boolean));
    return [...values].sort();
  }, [pases]);

  const kpiTotales = useMemo(
    () => computeKpiTotales(filterMisClientes(pases, filtroMisClientes)),
    [pases, filtroMisClientes],
  );

  const pasesConId = useMemo(() => {
    const sortedByDate = [...pases].sort((a, b) => {
      const dateA = a.createdAt?.toDate() || 0;
      const dateB = b.createdAt?.toDate() || 0;
      return dateA - dateB;
    });
    return sortedByDate.map((pase, index) => ({ ...pase, persistentId: index + 1 }));
  }, [pases]);

  const sortedPases = useMemo(() => {
    const items = [...pasesConId];
    items.sort((a, b) => compareValues(a[sortConfig.key], b[sortConfig.key], sortConfig.direction));
    return items;
  }, [pasesConId, sortConfig]);

  const hasActiveFilters = Object.keys(filters).length > 0;

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => {
      if (!value) {
        const { [field]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [field]: value };
    });
  }, []);

  const clearFilters = useCallback(() => setFilters({}), []);

  const filteredPases = useMemo(() => {
    const visibles = filterMisClientes(sortedPases, filtroMisClientes);
    if (!hasActiveFilters) return visibles;
    const cliente = filters.cliente?.trim().toLowerCase();
    const nroComitente = filters.nroComitente?.trim().toLowerCase();
    return visibles.filter(p => {
      if (cliente && !p.cliente?.toLowerCase().includes(cliente)) return false;
      if (nroComitente && !p.nroComitente?.toLowerCase().includes(nroComitente)) return false;
      if (filters.operacion && p.operacion !== filters.operacion) return false;
      if (filters.moneda && p.moneda !== filters.moneda) return false;
      if (filters.mercado && p.mercado !== filters.mercado) return false;
      if (filters.comercial && p.comercial !== filters.comercial) return false;
      if (filters.estado && p.estado !== filters.estado) return false;
      return true;
    });
  }, [sortedPases, filtroMisClientes, filters, hasActiveFilters]);

  const { topRef, contentRef, contentWidth, onTopScroll, onContentScroll } =
    useHorizontalScrollSync([filteredPases, isLoading]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
  };

  const handleDelete = async (pase) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este pase?')) return;
    try {
      await softDeletePase(pase, { userId, userName });
    } catch (error) {
      console.error('Error al eliminar el pase:', error);
      alert('No se pudo eliminar el pase. Intentá de nuevo.');
    }
  };

  const handleStatusChange = async (pase, newStatus) => {
    if (newStatus !== 'Pendiente' && !pase.monto) {
      alert('No se puede cambiar el estado. El pase debe tener un Monto definido.');
      const selectElement = document.querySelector(`select[data-pase-id="${pase.id}"]`);
      if (selectElement) selectElement.value = pase.estado;
      return;
    }
    if (userRole === 'Trader') {
      if (newStatus === 'Rechazada') {
        setPaseToReject(pase);
        setIsRejectionModalOpen(true);
        return;
      }
      if (newStatus === 'Hecha') {
        setOperarModal({ isOpen: true, pase, mode: 'operar', readOnly: false });
        return;
      }
    }
    try {
      await setPaseEstado(pase, newStatus, { userName });
    } catch (error) {
      console.error('Error al cambiar el estado del pase:', error);
    }
  };

  const handleRejectionSubmit = async () => {
    if (!rejectionComment.trim()) {
      alert('El comentario es obligatorio para rechazar un pase.');
      return;
    }
    try {
      await rechazarPase(paseToReject, rejectionComment, { userName });
    } catch (error) {
      console.error('Error al rechazar el pase:', error);
    } finally {
      setIsRejectionModalOpen(false);
      setRejectionComment('');
      setPaseToReject(null);
    }
  };

  const handleTasaUpdate = async (pase) => {
    const newTasa = parseFloat(parseTasaForState(editingTasaValue));
    if (isNaN(newTasa)) { setEditingTasaId(null); return; }
    try {
      await updatePase(pase.id, { tasa: newTasa });
    } catch (error) {
      console.error('Error al actualizar la tasa: ', error);
    } finally {
      setEditingTasaId(null);
    }
  };

  const handleEditClick = (pase) => {
    setEditingPase(pase);
    setIsModalOpen(true);
  };

  const openActivos = (pase, editable) => {
    setOperarModal({ isOpen: true, pase, mode: 'activos', readOnly: !editable });
  };

  const getRowStyle = (estado) => {
    switch (estado) {
      case 'En Proceso': return { backgroundColor: styles.colors.yellow };
      case 'Hecha': return { backgroundColor: styles.colors.green };
      case 'Rechazada': return { backgroundColor: styles.colors.red };
      default: return {};
    }
  };

  const renderCommentCell = (comment, title) => {
    const MAX_LENGTH = 25;
    if (!comment) return '-';
    if (comment.length > MAX_LENGTH) {
      return (
        <span style={styles.clickableText} title="Ver comentario completo" onClick={() => onViewComment(title, comment)}>
          {comment.substring(0, MAX_LENGTH)}...
        </span>
      );
    }
    return comment;
  };

  return (
    <div>
      <MobileCollapsibleSection title="Indicadores" summary="Tomadoras y colocadoras">
      {MONEDA_BUCKETS.map(bucket => (
        <div key={bucket} style={{ marginBottom: '2rem' }}>
          <h2 style={{ ...styles.logo, fontSize: '1.25rem', marginBottom: '1rem', borderBottom: '1px solid #E5E7EB', paddingBottom: '0.5rem' }}>
            Indicadores ({MONEDA_TITULO[bucket]})
          </h2>
          <div className="axon-grid-2" style={styles.kpiContainer}>
            <IndicatorCard title="Tomadoras Totales" value={kpiTotales[bucket].tomadorasTotales} currency={MONEDA_MOVIMIENTO[bucket]} />
            <IndicatorCard title="Tomadoras Operadas" value={kpiTotales[bucket].tomadorasOperadas} currency={MONEDA_MOVIMIENTO[bucket]} />
            <IndicatorCard title="Colocadoras Totales" value={kpiTotales[bucket].colocadorasTotales} currency={MONEDA_MOVIMIENTO[bucket]} />
            <IndicatorCard title="Colocadoras Operadas" value={kpiTotales[bucket].colocadorasOperadas} currency={MONEDA_MOVIMIENTO[bucket]} />
          </div>
        </div>
      ))}
      </MobileCollapsibleSection>

      {/* Sección de filtros */}
      <div style={filterContainerStyle}>
        <div style={{ ...filterHeaderStyle, gap: '0.75rem', ...(filtersPanel.isOpen ? {} : { borderBottom: 'none' }) }}>
          <CollapsibleHeader isMobile={filtersPanel.isMobile} isOpen={filtersPanel.isOpen} onToggle={filtersPanel.toggle} style={filterHeaderLeftStyle}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>
            <h3 style={filterTitleStyle}>Filtros</h3>
            {hasActiveFilters && <span style={filterBadgeStyle}>{Object.keys(filters).length}</span>}
          </CollapsibleHeader>
          {hasActiveFilters && (
            <button style={clearFiltersButtonStyle} onClick={clearFilters}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              Limpiar filtros
            </button>
          )}
        </div>
        {filtersPanel.isOpen && <div className="axon-grid-2" style={filterGridStyle}>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Cliente</label>
            <input style={filterInputStyle} type="text" placeholder="Nombre..." value={filters.cliente || ''} onChange={e => handleFilterChange('cliente', e.target.value)} />
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Nro Comitente</label>
            <input style={filterInputStyle} type="text" placeholder="Ej: 568" value={filters.nroComitente || ''} onChange={e => handleFilterChange('nroComitente', e.target.value)} />
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Operación</label>
            <select style={filterSelectFieldStyle} value={filters.operacion || ''} onChange={e => handleFilterChange('operacion', e.target.value)}>
              <option value="">Todas</option>
              {formOptions.operacion.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Moneda</label>
            <select style={filterSelectFieldStyle} value={filters.moneda || ''} onChange={e => handleFilterChange('moneda', e.target.value)}>
              <option value="">Todas</option>
              {formOptions.moneda.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Mercado</label>
            <select style={filterSelectFieldStyle} value={filters.mercado || ''} onChange={e => handleFilterChange('mercado', e.target.value)}>
              <option value="">Todos</option>
              {formOptions.mercado.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Comercial</label>
            <select style={filterSelectFieldStyle} value={filters.comercial || ''} onChange={e => handleFilterChange('comercial', e.target.value)}>
              <option value="">Todos</option>
              {uniqueComerciales.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div style={filterGroupStyle}>
            <label style={filterLabelStyle}>Estado</label>
            <select style={filterSelectFieldStyle} value={filters.estado || ''} onChange={e => handleFilterChange('estado', e.target.value)}>
              <option value="">Todos</option>
              {formOptions.estado.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '1rem', flexWrap: 'wrap' }}>
        <h2 style={{ ...styles.logo, fontSize: '1.5rem', margin: 0 }}>Planilla de Pases</h2>
        <button className="axon-fab" style={styles.button} onClick={() => { setEditingPase(null); setIsModalOpen(true); }}>
          + Agregar Pase
        </button>
      </div>

      <TopScrollbar scrollRef={topRef} onScroll={onTopScroll} width={contentWidth} />

      <div style={styles.tableContainer} ref={contentRef} onScroll={onContentScroll}>
        {isLoading ? <p style={{ textAlign: 'center', padding: '2rem' }}>Cargando pases...</p> :
          <table ref={stackedTable.ref} className={stackedTable.className} style={styles.table}>
            <thead>
              <tr>
                {tableHeaders.map(header => {
                  const isClickable = header.key !== 'actions' && header.sortable !== false;
                  return (
                    <th key={header.key} style={{ ...styles.th, ...(header.narrow ? styles.narrowCol : {}), cursor: isClickable ? 'pointer' : 'default' }} onClick={() => isClickable && requestSort(header.key)} title={header.tooltip || undefined}>
                      {header.label}
                      {isClickable && sortConfig.key === header.key && <SortIcon direction={sortConfig.direction} />}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredPases.length === 0 && (
                <tr><td colSpan={tableHeaders.length} style={{ ...styles.td, padding: '2rem', fontStyle: 'italic', color: '#465163' }}>No hay pases cargados.</td></tr>
              )}
              {filteredPases.map((pase) => {
                const rowStyle = getRowStyle(pase.estado);
                const canEdit = ['Comercial', 'Trader', 'MiddleOffice'].includes(userRole) && pase.estado === 'Pendiente';
                // Middle office elimina los que cargó él en apoyo de un comercial.
                const canDelete = pase.estado === 'Pendiente'
                  && ((userRole === 'Comercial' && pase.comercial === userName)
                    || (userRole === 'MiddleOffice' && pase.creadoPorId === userId));
                const canEditTasa = userRole === 'Trader' && pase.estado === 'Hecha';
                const canEditActivos = userRole === 'Trader' && (pase.estado === 'En Proceso' || pase.estado === 'Hecha');
                const cantActivos = (pase.activos || []).length;

                return (
                  <tr key={pase.id} style={rowStyle}>
                    <td style={styles.td}>{pase.persistentId}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <button style={{ ...styles.iconButton, color: canEdit ? styles.primary : '#D1D5DB' }} onClick={() => handleEditClick(pase)} disabled={!canEdit} title={canEdit ? 'Editar' : 'No tiene permisos'}><PencilIcon /></button>
                      <button style={{ ...styles.iconButton, color: canDelete ? styles.redText : '#D1D5DB' }} onClick={() => handleDelete(pase)} disabled={!canDelete} title={canDelete ? 'Eliminar' : 'No tiene permisos'}><TrashIcon /></button>
                    </td>
                    <td style={styles.td}>{pase.createdAt?.toDate().toLocaleDateString('es-AR')}</td>
                    <td style={{ ...styles.td, ...styles.narrowCol }}>{formatHoraForDisplay(pase.createdAt)}</td>
                    <td style={styles.td}><OperacionBadge operacion={pase.operacion} estado={pase.estado} /></td>
                    <td style={styles.td}>{formatComitenteForDisplay(pase.nroComitente)}</td>
                    <td style={styles.td}>{renderCommentCell(pase.cliente, 'Cliente')}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{formatCurrency(pase.monto, pase.moneda)}</td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{pase.montoOperado ? formatCurrency(pase.montoOperado, pase.moneda) : '-'}</td>
                    <td style={styles.td}>{formatVencimiento(pase.fechaVencimiento)}</td>
                    <td style={{ ...styles.td, textAlign: 'right', cursor: canEditTasa ? 'pointer' : 'default', backgroundColor: editingTasaId === pase.id ? '#F9FAFB' : 'transparent' }} onClick={() => { if (canEditTasa) { setEditingTasaId(pase.id); setEditingTasaValue(formatNumberForInput(pase.tasa)); } }}>
                      {editingTasaId === pase.id ? (
                        <input type="text" inputMode="decimal" value={editingTasaValue} onChange={(e) => setEditingTasaValue(e.target.value)} onBlur={() => handleTasaUpdate(pase)} onKeyDown={(e) => { if (e.key === 'Enter') handleTasaUpdate(pase); if (e.key === 'Escape') setEditingTasaId(null); }} autoFocus style={{ ...styles.input, textAlign: 'right', height: 'auto', padding: '0.25rem', width: '80px' }} />
                      ) : formatTasaForDisplay(pase.tasa)}
                    </td>
                    <td style={{ ...styles.td, textAlign: 'right' }}>{pase.tasaOperada ? formatTasaForDisplay(pase.tasaOperada) : '-'}</td>
                    <td style={styles.td}>{pase.moneda}</td>
                    <td style={styles.td}>{pase.mercado}</td>
                    <td style={styles.td}>{pase.comercial}</td>
                    <td style={styles.td}>{pase.operador}</td>
                    <td style={styles.td}>{userRole === 'Trader' ? (
                      <select data-pase-id={pase.id} value={pase.estado} onChange={(e) => handleStatusChange(pase, e.target.value)} style={{ ...styles.select, marginTop: 0, padding: '0.25rem', backgroundColor: 'rgba(255,255,255,0.6)' }}>
                        {formOptions.estado.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    ) : pase.estado}</td>
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      {cantActivos > 0 ? (
                        <span style={activosBadgeStyle} title="Ver activos del pase" onClick={() => openActivos(pase, canEditActivos)}>
                          {cantActivos} {cantActivos === 1 ? 'activo' : 'activos'}
                        </span>
                      ) : '-'}
                    </td>
                    <td style={styles.td}>{renderCommentCell(pase.comentarioComercial, 'Comentario Comercial')}</td>
                    <td style={styles.td}>{renderCommentCell(pase.comentarioTrader, 'Comentario del Trader')}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        }
      </div>

      <PaseFormModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingPase(null); }}
        clientesDb={clientesDb}
        especies={especies}
        userRole={userRole}
        userName={userName}
        userId={userId}
        editingPase={editingPase}
      />

      <PaseOperarModal
        isOpen={operarModal.isOpen}
        onClose={() => setOperarModal({ isOpen: false, pase: null, mode: 'operar', readOnly: false })}
        pase={operarModal.pase}
        mode={operarModal.mode}
        readOnly={operarModal.readOnly}
        userName={userName}
      />

      <Modal
        isOpen={isRejectionModalOpen}
        title="Motivo de Rechazo"
        maxWidth="500px"
        footer={<>
          <button onClick={() => { setIsRejectionModalOpen(false); setPaseToReject(null); }} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cancelar</button>
          <button onClick={handleRejectionSubmit} style={fieldStyles.button}>Confirmar Rechazo</button>
        </>}
      >
        <p style={{ margin: '0 0 1rem 0', color: styles.colors.secondary }}>Por favor, ingrese el motivo por el cual se rechaza este pase.</p>
        <textarea value={rejectionComment} onChange={(e) => setRejectionComment(e.target.value)} style={{ ...fieldStyles.textarea, minHeight: '100px' }} autoFocus />
      </Modal>
    </div>
  );
};

export default PasesPlanilla;
